// Funções puras da timeline do chat com clientes (sem Vue/Vuex), testadas em
// tests/customerChat.test.js. CommonJS de propósito: o mesmo arquivo é importado
// pelo webpack e pelo runner `node` dos testes (mesmo padrão de contractFinancial).

const LOCAL_STATUS_SENDING = 'sending'
const LOCAL_STATUS_ERROR = 'error'

const hasSeq = message => !!message && typeof message.seq === 'number' && Number.isFinite(message.seq)

// Mesma mensagem = mesmo id; ou mesmo clientMessageId (otimista x confirmada / eco do WS);
// ou mesmo seq na mesma conversa (seq é único por conversa no servidor).
const isSameMessage = (a, b) => {
  if (a.id && b.id && a.id === b.id) return true
  if (a.clientMessageId && b.clientMessageId && a.clientMessageId === b.clientMessageId) return true
  return hasSeq(a) && hasSeq(b) && a.seq === b.seq && a.conversationId === b.conversationId
}

// Ordem canônica é `seq`; mensagens ainda sem seq (otimistas) ficam no fim, na ordem
// em que foram criadas (Array#sort é estável).
const compareMessages = (a, b) => {
  const aHas = hasSeq(a)
  const bHas = hasSeq(b)
  if (aHas && bHas) return a.seq - b.seq
  if (aHas) return -1
  if (bHas) return 1
  return 0
}

/**
 * Junta mensagens novas à lista atual, deduplicando e ordenando por seq.
 * Uma mensagem confirmada (com seq) nunca é rebaixada para otimista.
 * Não muta os argumentos.
 */
const mergeMessages = (current, incoming) => {
  const result = (current || []).slice()
  const additions = incoming || []
  additions.forEach(message => {
    if (!message) return
    // Pode casar com mais de um item (ex.: eco do WS sem clientMessageId já inserido por
    // id + a otimista por clientMessageId). Fica um só, na posição do primeiro.
    const matches = []
    result.forEach((existing, index) => {
      if (isSameMessage(existing, message)) matches.push(index)
    })
    if (!matches.length) {
      result.push(message)
      return
    }
    if (!hasSeq(message) && matches.some(index => hasSeq(result[index]))) return
    result[matches[0]] = message
    for (let i = matches.length - 1; i > 0; i -= 1) result.splice(matches[i], 1)
  })
  return result.sort(compareMessages)
}

const maxSeq = messages => (messages || []).reduce(
  (max, message) => (hasSeq(message) && message.seq > max ? message.seq : max),
  0,
)

const minSeq = messages => (messages || []).reduce(
  (min, message) => (hasSeq(message) && (min === null || message.seq < min) ? message.seq : min),
  null,
)

// Mensagens com seq <= readFromSeq não são visíveis para quem pergunta (convidado
// entra a partir de um ponto da timeline). Otimistas (sem seq) são sempre visíveis.
const visibleMessages = (messages, readFromSeq) => {
  const from = Number(readFromSeq) || 0
  return (messages || []).filter(message => !hasSeq(message) || message.seq > from)
}

const staffPrincipal = userId => (userId === undefined || userId === null || userId === '' ? null : `core:${userId}`)

const isOwnMessage = (message, principal) => !!principal
  && !!message
  && message.senderType === 'STAFF'
  && message.senderPrincipal === principal

const joinLabel = (name, unitLabel) => [name, unitLabel].filter(Boolean).join(' · ')

const senderLabel = message => joinLabel(message && message.senderName, message && message.senderUnitLabel)

const participantLabel = participant => joinLabel(participant && participant.name, participant && participant.unitLabel)

// Id da última mensagem de staff já lida pelo cliente (para o indicador "Lida").
// Marca de entrega de uma mensagem do atendente, como no WhatsApp:
// 'sending' (relógio), 'sent' (✓ — o servidor confirmou, tem seq), 'read' (✓✓ — o cliente leu
// até ela), 'error'. Mensagens que não são do staff não têm marca (null).
const DELIVERY_SENDING = 'sending'
const DELIVERY_SENT = 'sent'
const DELIVERY_READ = 'read'
const DELIVERY_ERROR = 'error'

const deliveryStatus = (message, customerLastReadSeq) => {
  if (!message || message.senderType !== 'STAFF') return null
  if (message.localStatus === LOCAL_STATUS_ERROR) return DELIVERY_ERROR
  if (message.localStatus === LOCAL_STATUS_SENDING || !hasSeq(message)) return DELIVERY_SENDING
  const readSeq = Number(customerLastReadSeq) || 0
  return message.seq <= readSeq ? DELIVERY_READ : DELIVERY_SENT
}

const buildOptimisticMessage = ({
  conversationId, clientMessageId, body, principal, senderName, senderUnitLabel, now,
}) => ({
  conversationId,
  clientMessageId,
  body,
  senderType: 'STAFF',
  senderPrincipal: principal || undefined,
  senderName,
  senderUnitLabel,
  createdAt: new Date(now || Date.now()).toISOString(),
  localStatus: LOCAL_STATUS_SENDING,
})

module.exports = {
  LOCAL_STATUS_SENDING,
  LOCAL_STATUS_ERROR,
  hasSeq,
  isSameMessage,
  compareMessages,
  mergeMessages,
  maxSeq,
  minSeq,
  visibleMessages,
  staffPrincipal,
  isOwnMessage,
  senderLabel,
  participantLabel,
  DELIVERY_SENDING,
  DELIVERY_SENT,
  DELIVERY_READ,
  DELIVERY_ERROR,
  deliveryStatus,
  buildOptimisticMessage,
}
